import React, { useEffect, useRef, useState } from "react";
import { useParams, useLoaderData } from "react-router-dom";
import mermaid from "mermaid";
import ReactJoyride, { ACTIONS, EVENTS, STATUS } from "react-joyride";

import axiosBackend from "../../../../core/api/backend";
import Loader from "../../../../components/Loader";
import API_STATES from "../../../../api-states";
import ErrorPage from "../../../error";
import steps from "./help-steps";
import MarkIntroductionTourDone from "../../../../core/onboarding/mark-introduction-tour-done";

export const ResourceERDIntroductionIdentifier = "resources/resource-erd-introduction";

const ZOOM_STEP = 0.2;
const ZOOM_MIN = 0.2;
const ZOOM_MAX = 4;

function ResourceERD() {
    mermaid.initialize({ startOnLoad: false, theme: "default" });

    const params = useParams();
    const { shouldRun, ...loaderData } = useLoaderData();
    const appName = loaderData?.[0]?.title;

    const diagramRef = useRef(null);
    const containerRef = useRef(null);

    const [status, setStatus] = useState(API_STATES.LOADING);
    const [error, setError] = useState(null);

    const [erdDefinition, setErdDefinition] = useState(null);
    const [zoom, setZoom] = useState(1);
    const [naturalSize, setNaturalSize] = useState(null);
    const [diagramReady, setDiagramReady] = useState(false);

    const [showTour, setShowTour] = useState(false);
    const [stepIndex, setStepIndex] = useState(0);

    const handleJoyrideCallback = (data) => {
        if ([STATUS.FINISHED, STATUS.SKIPPED, STATUS.CLOSE].includes(data.status) || ACTIONS.CLOSE === data.action) {
            setShowTour(false);
            setStepIndex(0);
            if (shouldRun) {
                MarkIntroductionTourDone({ identifier: ResourceERDIntroductionIdentifier });
            }
        } else if ([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND].includes(data.type)) {
            setStepIndex(data.index + (data.action === ACTIONS.PREV ? -1 : 1));
        }
    };

    useEffect(() => {
        if (shouldRun) setShowTour(true);
    }, [shouldRun]);

    useEffect(() => {
        axiosBackend({
            method: "POST",
            url: `/service/resources/${params.appUUID}/erd`,
            data: {},
        })
            .then((response) => {
                setErdDefinition(response.data.results[0].erd);
                setStatus(API_STATES.LOADED);
            })
            .catch((err) => {
                console.error(err);
                setError(err?.response?.data?.message || "Failed to load ERD.");
                setStatus(API_STATES.ERROR);
            });
    }, [params.appUUID]);

    useEffect(() => {
        if (status === API_STATES.LOADED && erdDefinition && diagramRef.current) {
            mermaid
                .render("erd-diagram", erdDefinition)
                .then(({ svg }) => {
                    diagramRef.current.innerHTML = svg;

                    const svgEl = diagramRef.current.querySelector("svg");
                    if (!svgEl) return;

                    const naturalWidth =
                        parseFloat(svgEl.style.maxWidth) ||
                        svgEl.viewBox?.baseVal?.width ||
                        svgEl.getBoundingClientRect().width;
                    const naturalHeight =
                        svgEl.viewBox?.baseVal?.height ||
                        svgEl.getBoundingClientRect().height;

                    setNaturalSize({ width: naturalWidth, height: naturalHeight });

                    svgEl.style.maxWidth = "";
                    svgEl.setAttribute("width", naturalWidth);
                    svgEl.setAttribute("height", naturalHeight);

                    const containerWidth = containerRef.current?.clientWidth || naturalWidth;
                    const fitZoom = Math.min(containerWidth / naturalWidth, ZOOM_MAX);
                    setZoom(fitZoom);
                    setDiagramReady(true);
                })
                .catch((err) => {
                    console.error(err);
                    setError("Failed to render ERD diagram. Please try again in a little while.");
                    setStatus(API_STATES.ERROR);
                });
        }
    }, [status, erdDefinition]);

    // Resize the SVG element whenever the zoom level or the diagram's natural dimensions change.
    useEffect(() => {
        if (!naturalSize || !diagramRef.current) return;
        const svgEl = diagramRef.current.querySelector("svg");
        if (!svgEl) return;
        svgEl.setAttribute("width", naturalSize.width * zoom);
        svgEl.setAttribute("height", naturalSize.height * zoom);
    }, [zoom, naturalSize]);

    // Once the diagram is ready, fit it to the container width by setting an initial zoom level.
    useEffect(() => {
        if (!diagramReady || !naturalSize || !containerRef.current) return;
        const containerWidth = containerRef.current.clientWidth;
        if (containerWidth > 0) {
            setZoom(Math.min(containerWidth / naturalSize.width, ZOOM_MAX));
        }
    }, [diagramReady]);

    function downloadAsMermaid() {
        if (!erdDefinition) return;
        const blob = new Blob([erdDefinition], { type: "text/plain;charset=utf-8" });
        const a = document.createElement("a");
        a.download = `${appName || "erd"}.mmd`;
        a.href = URL.createObjectURL(blob);
        a.click();
        URL.revokeObjectURL(a.href);
    }

    function downloadAsPng() {
        const svgEl = diagramRef.current?.querySelector("svg");
        if (!svgEl) return;

        const origW = svgEl.getAttribute("width");
        const origH = svgEl.getAttribute("height");
        svgEl.setAttribute("width", naturalSize.width);
        svgEl.setAttribute("height", naturalSize.height);

        const svgData = new XMLSerializer().serializeToString(svgEl);

        svgEl.setAttribute("width", origW);
        svgEl.setAttribute("height", origH);

        const url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgData)}`;

        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement("canvas");
            canvas.width = naturalSize.width;
            canvas.height = naturalSize.height;

            const ctx = canvas.getContext("2d");
            ctx.fillStyle = "#ffffff";
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0);

            const a = document.createElement("a");
            a.download = `${appName || "erd"}.png`;
            a.href = canvas.toDataURL("image/png");
            a.click();
        };
        img.src = url;
    }

    return (
        <>
            <ReactJoyride
                callback={handleJoyrideCallback}
                run={showTour}
                stepIndex={stepIndex}
                steps={steps}
                continuous={true}
                showSkipButton={true}
                showProgress={true}
                styles={{ options: { zIndex: 10000 } }}
                disableScrollParentFix={true}
            />

            {diagramReady && (
                <div className="col-lg-12 pb-2">
                    <button id="toggle-help" className="btn btn-gray" onClick={() => setShowTour(true)}>
                        <span className="far fa-question-circle fa-lg"></span>
                    </button>
                </div>
            )}

            {(status === API_STATES.LOADING || (status === API_STATES.LOADED && !diagramReady)) && (
                <div className="row">
                    <div className="col-sm-12 pr-4 pl-4 pt-2 pb-2 ml-2">
                        <Loader />
                    </div>
                </div>
            )}

            {status === API_STATES.ERROR && (
                <ErrorPage hideSidebar={true} message={error} />
            )}

            {status === API_STATES.LOADED && (
                <div className="col-sm-12" style={{ display: diagramReady ? undefined : "none" }}>
                    <div className="row">
                        <div className="col-sm-12">
                            <div className="panel p-3 mb-0 border-bottom">
                                <div className="btn-group float-right" id="erd-zoom-controls">

                                    <div className="dropdown mr-2" id="erd-download">
                                        <button
                                            className="btn btn-xs btn-white dropdown-toggle"
                                            data-toggle="dropdown"
                                            aria-haspopup="true"
                                            aria-expanded="false"
                                        >
                                            <i className="fas fa-download"></i>&nbsp;Download
                                        </button>
                                        <div className="dropdown-menu dropdown-menu-right">
                                            <button className="dropdown-item" onClick={downloadAsPng}>
                                                <i className="fas fa-image"></i>&nbsp;PNG image
                                            </button>
                                            <button className="dropdown-item" onClick={downloadAsMermaid}>
                                                <i className="fas fa-code"></i>&nbsp;Mermaid code (.mmd)
                                            </button>
                                        </div>
                                    </div>

                                    <button
                                        className="btn btn-xs btn-white"
                                        onClick={() => setZoom(z => Math.max(+(z - ZOOM_STEP).toFixed(2), ZOOM_MIN))}
                                        title="Zoom out"
                                    >
                                        <i className="fas fa-search-minus"></i>
                                    </button>
                                    <button
                                        className="btn btn-xs btn-white"
                                        onClick={() => {
                                            if (naturalSize && containerRef.current) {
                                                setZoom(Math.min(containerRef.current.clientWidth / naturalSize.width, ZOOM_MAX));
                                            }
                                        }}
                                        title="Fit to window"
                                        style={{ minWidth: "48px" }}
                                    >
                                        {Math.round(zoom * 100)}%
                                    </button>
                                    <button
                                        className="btn btn-xs btn-white"
                                        onClick={() => setZoom(z => Math.min(+(z + ZOOM_STEP).toFixed(2), ZOOM_MAX))}
                                        title="Zoom in"
                                    >
                                        <i className="fas fa-search-plus"></i>
                                    </button>


                                </div>

                                <div className="row">
                                    <div className="col-lg-12">
                                        <h2 className="mt-0">Entity Relationship Diagram</h2>
                                        <p>Shows how your resources relate to each other. Boxes are resources, lines are relationships created by <strong>LOOKUP</strong> fields.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="panel p-2" style={{ position: "relative" }} id="erd-diagram-container">
                        <div
                            ref={containerRef}
                            style={{ height: "calc(100vh - 220px)", overflow: "auto" }}
                        >
                            <div
                                ref={diagramRef}
                                style={{ padding: "16px", display: "inline-block" }}
                            />
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default ResourceERD;
